/**
 * Contract Status Import — Review Queue.
 *
 * Rows that couldn't be applied during import land here:
 *   • unmatched_agent    — the report NPN isn't in the system → link to an
 *                          existing agent, then apply (update/create appt).
 *   • missing_appointment — the agent has no appointment with that carrier →
 *                          create it, or dismiss.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, ArrowLeft, CheckCircle2, X, ListChecks } from 'lucide-react';
import { contractStatusImportAPI, agentAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';

export default function ContractStatusReviewPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [query, setQuery] = useState({});     // review_id → search string
  const [results, setResults] = useState({}); // review_id → [agents]

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await contractStatusImportAPI.review();
      setRows(r.data?.rows || []);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load review queue'));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const searchAgent = async (id, q) => {
    setQuery((s) => ({ ...s, [id]: q }));
    if (!q || q.length < 2) { setResults((s) => ({ ...s, [id]: [] })); return; }
    try {
      const r = await agentAPI.list({ search: q, limit: 8 });
      const list = r.data?.agents || r.data || [];
      setResults((s) => ({ ...s, [id]: Array.isArray(list) ? list.slice(0, 8) : [] }));
    } catch { /* ignore */ }
  };

  const act = async (row, action, target_npn) => {
    setBusyId(row.review_id);
    try {
      const r = await contractStatusImportAPI.resolveReview(row.review_id, { action, target_npn });
      toast.success(r.data.resolution === 'dismissed' ? 'Dismissed'
        : r.data.resolution === 'created_appointment' ? 'Appointment created'
        : 'Appointment updated');
      setRows((rs) => rs.filter((x) => x.review_id !== row.review_id));
    } catch (e) {
      toast.error(getErrorMessage(e, 'Action failed'));
    } finally { setBusyId(null); }
  };

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto" data-testid="contract-status-review-page">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <div className="h-10 w-10 rounded-xl bg-amber-500/10 grid place-items-center">
            <ListChecks className="h-5 w-5 text-amber-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Contract Status — Review Queue</h1>
            <p className="text-sm text-slate-500">Rows from imports that need an agent link or a new appointment</p>
          </div>
        </div>
        <Button variant="outline" onClick={() => navigate('/contracts/import-status')} data-testid="review-back-btn">
          <ArrowLeft size={14} className="mr-1.5" /> Back to import
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : rows.length === 0 ? (
        <Card><CardContent className="text-center py-16 text-slate-500" data-testid="review-empty">
          <CheckCircle2 className="h-10 w-10 mx-auto text-emerald-300 mb-2" />
          Nothing to review — every imported row was applied.
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {rows.map((row) => (
            <Card key={row.review_id} data-testid={`review-row-${row.review_id}`}>
              <CardContent className="py-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-sm">
                    <span className="font-mono font-medium">{row.npn}</span>
                    <span className="text-slate-400"> · </span>
                    <span>{row.carrier_name || row.carrier_raw || '—'}</span>
                    {row.product && <span className="text-slate-400 text-xs"> · {row.product}</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={row.kind === 'unmatched_agent'
                      ? 'bg-rose-50 text-rose-700 border-rose-200'
                      : 'bg-amber-50 text-amber-700 border-amber-200'}>
                      {row.kind === 'unmatched_agent' ? 'Unknown agent' : 'Missing appointment'}
                    </Badge>
                    {row.target_status && <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200">→ {row.target_status}</Badge>}
                  </div>
                </div>
                <div className="text-xs text-slate-500">{row.reason} · report status: {row.status_raw || '—'}</div>

                {row.kind === 'unmatched_agent' ? (
                  <div>
                    <input
                      value={query[row.review_id] || ''}
                      onChange={(e) => searchAgent(row.review_id, e.target.value)}
                      placeholder="Link to an agent — search by name…"
                      className="w-full h-8 rounded-md border border-slate-200 px-2 text-sm"
                      data-testid={`review-agent-search-${row.review_id}`}
                    />
                    {(results[row.review_id] || []).length > 0 && (
                      <ul className="mt-1 border rounded-md divide-y">
                        {results[row.review_id].map((ag) => (
                          <li key={ag.npn}>
                            <button
                              className="w-full text-left px-2 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50"
                              disabled={busyId === row.review_id || !row.carrier_id || !row.target_status}
                              onClick={() => act(row, 'link', ag.npn)}
                            >
                              Link → {ag.first_name} {ag.last_name} <span className="text-slate-400 font-mono text-xs">· {ag.npn}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                    {(!row.carrier_id || !row.target_status) && (
                      <p className="text-xs text-rose-500 mt-1">Can't apply: carrier unmatched or status unmapped. Dismiss or fix the source and re-import.</p>
                    )}
                    <div className="mt-2">
                      <Button variant="ghost" size="sm" onClick={() => act(row, 'dismiss')} disabled={busyId === row.review_id} data-testid={`review-dismiss-${row.review_id}`}>
                        <X size={13} className="mr-1" /> Dismiss
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => act(row, 'create')}
                      disabled={busyId === row.review_id || !row.carrier_id || !row.target_status}
                      data-testid={`review-create-${row.review_id}`}
                    >
                      {busyId === row.review_id ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <CheckCircle2 size={13} className="mr-1.5" />}
                      Create appointment ({row.target_status})
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => act(row, 'dismiss')} disabled={busyId === row.review_id} data-testid={`review-dismiss-${row.review_id}`}>
                      <X size={13} className="mr-1" /> Dismiss
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
